import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
import { Button } from "@/components/ui/button/button";
import { Textarea } from "@/components/ui/textarea/textarea";
import { Table } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { CheckIcon } from "lucide-react";
import { UploadAttachment } from "./upload-attachement";

interface AddSubtaskProps<TData> {
  table: Table<TData>;
  assignees: {
    value: string;
    label: string;
  }[];
}

export const AddSubtask = <TData,>({
  table,
  assignees,
}: AddSubtaskProps<TData>) => {
  const [open, setOpen] = useState(false);

  const [rowData, setRowData] = useState<{
    description: string;
    recipient: {
      value: string;
      label: string;
    };
    assignee: string;
    files: string[];
  }>({
    description: "",
    recipient: {
      value: "",
      label: "",
    },
    assignee: "",
    files: [],
  });

  const onChangeFiles = (newFiles: string[]) => {
    setRowData((prev) => {
      return {
        ...prev,
        files: newFiles,
      };
    });
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>
          <Button type="button" size="sm">
            Add Subtask
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Add Subtask</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="description">Description</label>
              <Textarea
                id="description"
                name="description"
                value={rowData.description}
                onChange={(e) => {
                  setRowData((prev) => {
                    return {
                      ...prev,
                      [e.target.name]: e.target.value,
                    };
                  });
                }}
              />
            </div>

            <div>
              <p className="mb-1">Assignee</p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      "w-full justify-between",
                      !rowData?.recipient?.value && "text-muted-foreground"
                    )}
                  >
                    {rowData?.recipient?.value
                      ? assignees.find(
                          (assignee) =>
                            assignee?.value === rowData?.recipient?.value
                        )?.label
                      : "Select Assignee"}
                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search Assignee..."
                      className="h-9"
                    />
                    <CommandEmpty>No assignee found.</CommandEmpty>
                    <CommandGroup>
                      <CommandList>
                        {assignees?.map((assignee) => (
                          <CommandItem
                            value={assignee?.label}
                            key={assignee?.value}
                            onSelect={() => {
                              // form.setValue("language", language.value);
                              setRowData((prev) => {
                                return {
                                  ...prev,
                                  recipient: assignee,
                                  assignee: assignee?.label,
                                };
                              });
                            }}
                          >
                            {assignee.label}
                            <CheckIcon
                              className={cn(
                                "ml-auto h-4 w-4",
                                assignee?.value === rowData?.recipient.value
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandList>
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="">
              <UploadAttachment
                value={rowData.files}
                onChange={onChangeFiles}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // @ts-ignore
                table.options.meta?.addRow(rowData);

                setRowData({
                  description: "",
                  recipient: {
                    value: "",
                    label: "",
                  },
                  assignee: "",
                  files: [],
                });

                setOpen(false);
              }}
            >
              Add
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
